import { parseMoneyToCents, type MoneyInput } from './money'

/**
 * Where a franchise delivers, and what it charges to go there.
 *
 * Four screens need this answer — the order builder, the restaurant till, the
 * dispatch board and the zone editor — so it lives here (§5). It is also money
 * and geometry, which is to say it is the part of the order that is wrong
 * silently: a point judged just outside a polygon does not raise an error, it
 * quotes the wrong fee and nobody notices until the month's takings are short.
 *
 * Ported from legacy `lib/delivery-zones-store.ts`, with the fee arithmetic
 * moved to integer cents (§14.7).
 */

export interface LatLng {
  lat: number
  lng: number
}

export interface PriceTier {
  id: string
  /** Inclusive lower bound, in euros as the API sends it. */
  orderFrom: MoneyInput
  /** Exclusive upper bound; `null` means "and everything above". */
  orderTo: MoneyInput | null
  deliveryFee: MoneyInput
}

export interface DeliveryZone {
  id: string
  name: string
  color: string
  enabled: boolean
  type: 'circle' | 'polygon'
  center?: LatLng
  /** Metres. */
  radius?: number
  polygon?: LatLng[]
  minOrder: MoneyInput
  tiers: PriceTier[]
}

const EARTH_RADIUS_METRES = 6_371_000
const MINIMUM_POLYGON_POINTS = 3

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180
}

/** Great-circle distance. Zone radii are a few kilometres, so this is exact enough. */
export function haversineMetres(from: LatLng, to: LatLng): number {
  const deltaLat = toRadians(to.lat - from.lat)
  const deltaLng = toRadians(to.lng - from.lng)
  const chordHalf =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(deltaLng / 2) ** 2
  return EARTH_RADIUS_METRES * 2 * Math.atan2(Math.sqrt(chordHalf), Math.sqrt(1 - chordHalf))
}

/**
 * Ray casting, counting crossings of a horizontal ray to the west.
 *
 * A point exactly on an edge is not promised either answer — floating point
 * decides — and that is true of every implementation of this. The zones a
 * franchise draws have streets between them, so it does not arise in practice.
 */
export function isPointInPolygon(point: LatLng, polygon: LatLng[]): boolean {
  let inside = false
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
    const current = polygon[index]
    const before = polygon[previous]
    if (!current || !before) continue

    const straddles = current.lat > point.lat !== before.lat > point.lat
    if (!straddles) continue

    const crossingLng =
      ((before.lng - current.lng) * (point.lat - current.lat)) / (before.lat - current.lat) +
      current.lng
    if (point.lng < crossingLng) inside = !inside
  }
  return inside
}

/**
 * The first enabled zone containing the point.
 *
 * **Not the server's rule - §9.801.** It takes the first zone in list order and measures a circle as a true circle;
 * the server takes the zones whose flat-degree area holds the point and settles them with `pickZone`. Nothing calls
 * this any more: a zone for a point is `zoneServing`. Kept with its tests rather than deleted in the same change.
 *
 * First, not nearest and not smallest: where two zones overlap, the order of
 * the list decides, and the zone editor's order is therefore a pricing
 * decision. Ported as-is — changing it would re-price every address in an
 * overlap without anyone asking for it.
 */
export function findZoneForPoint(point: LatLng, zones: DeliveryZone[]): DeliveryZone | null {
  for (const zone of zones) {
    if (!zone.enabled) continue

    if (zone.type === 'circle' && zone.center && zone.radius != null) {
      if (haversineMetres(point, zone.center) <= zone.radius) return zone
      continue
    }

    if (
      zone.type === 'polygon' &&
      zone.polygon &&
      zone.polygon.length >= MINIMUM_POLYGON_POINTS &&
      isPointInPolygon(point, zone.polygon)
    ) {
      return zone
    }
  }
  return null
}

/**
 * The delivery fee in cents, or `null` when the order is under the zone's
 * minimum — which means *we do not deliver this*, not *delivery is free*. The
 * caller has to tell those apart, so the two answers are different types.
 *
 * Tiers are half-open: `[orderFrom, orderTo)`. A gap between tiers falls
 * through to the highest tier rather than to zero, because a franchise that
 * mis-enters its bands should over-charge rather than deliver for nothing.
 */
export function deliveryFeeCents(orderTotal: MoneyInput, zone: DeliveryZone): number | null {
  const totalCents = parseMoneyToCents(orderTotal)
  if (totalCents < parseMoneyToCents(zone.minOrder)) return null

  const byLowerBound = [...zone.tiers].sort(
    (left, right) => parseMoneyToCents(left.orderFrom) - parseMoneyToCents(right.orderFrom),
  )

  for (const tier of byLowerBound) {
    const from = parseMoneyToCents(tier.orderFrom)
    const to = tier.orderTo === null || tier.orderTo === undefined ? null : parseMoneyToCents(tier.orderTo)
    if (totalCents >= from && (to === null || totalCents < to)) {
      return parseMoneyToCents(tier.deliveryFee)
    }
  }

  const highest = byLowerBound[byLowerBound.length - 1]
  return highest ? parseMoneyToCents(highest.deliveryFee) : 0
}

/** The palette the zone editor offers. Ported so a re-drawn zone keeps its colour. */
export const ZONE_COLORS = [
  '#3b82f6',
  '#f97316',
  '#22c55e',
  '#a855f7',
  '#ef4444',
  '#eab308',
  '#06b6d4',
  '#ec4899',
]

/*
 * ── Which of several zones an address belongs to - §10A.121, moved here 2026-09-17 ──────────────
 *
 * **It lived in `customer-checkout/src/delivery.ts` and now two apps need it.** §9.346 made the
 * manager's phone-order picker resolve a tie of equally-named zones, and the tie-break has to be the
 * same four steps the checkout and `DeliveryZoneService.pickZone` use - a third rule would be a
 * third delivery fee. `customer-checkout` re-exports it, so every existing import still resolves.
 */
const METRES_PER_DEGREE_FOR_TIES = 111_320

/** Diacritics out, lower-cased, whitespace collapsed - so `Bánov` matches `banov`. */
function fold(value: string | null | undefined): string {
  // Undefined as well as null: a caller may hand this a zone whose `name` its own parser dropped,
  // and a crash inside a tie-break is worse than an empty string - §9.346 found exactly that.
  if (value === null || value === undefined) return ''
  return value
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

/** The same flat-degree arithmetic `withinRadius` uses, so the tie-break cannot pick a zone the point is not in. */
function metresToCentre(zone: ZoneTieBreakInput, lat: number, lng: number): number {
  // `DeliveryZone` here declares `center` optional, where the checkout's local type had it nullable -
  // so this guards both, and an unplaceable zone can never win a tie-break it cannot be measured for.
  const centre = zone.center
  if (centre === null || centre === undefined) return Number.MAX_VALUE
  return Math.hypot(lat - centre.lat, lng - centre.lng) * METRES_PER_DEGREE_FOR_TIES
}

/**
 * Which of several containing zones an address belongs to - §10A.121.
 *
 * **The owner gave the rule and then corrected it, and the correction is the one that matters.**
 * First: *"when two doručovacie zóny colides with our address, use that one, who is closer to Adresa
 * depota"*. Then: *"when adresa is in bánov, but we also have zóna overlap from nitriansky hrádok,
 * then use bánov"*. Those are different rules - the second says the **address names the place**, and
 * that beats any distance. Both are wanted, in that order.
 *
 * Measured on their own data: **8 of the 465 zone pairs overlap**, including *Bánov* with *Nitriansky
 * Hrádok* - their example. This function took the **first match in list order** and the server took
 * `created_at` order, so the winner was an accident of insertion order **and the two could disagree
 * about which fee applies**.
 *
 * <p>`DeliveryZoneService.pickZone` is the same four steps in Java and
 * `OverlappingZonesTest` carries the same cases under the same names. Keeping them in step is the
 * whole point: a client and a server that resolve a zone differently is a delivery fee that differs
 * from the one the customer agreed to.
 */
/**
 * All the tie-break needs of a zone: a name to look for in the address, and a circle to measure.
 *
 * **Declared rather than taking `DeliveryZone`**, because the two callers do not share a type -
 * `customer-checkout` has its own, looser one, and the manager builds a partial. Asking for the whole
 * interface made 20 of the checkout's own fixtures stop compiling the moment this moved here, which
 * is a signature demanding more than the function reads.
 */
export interface ZoneTieBreakInput {
  name?: string | null
  center?: LatLng | null
  radius?: number | null
}

export function pickZone<T extends ZoneTieBreakInput>(
  candidates: readonly T[],
  address: string | null,
  depot: { lat: number; lng: number } | null,
): T | null {
  if (candidates.length === 0) return null
  if (candidates.length === 1) return candidates[0] ?? null

  // Step 3 - the address names one of them, the longer name winning where one contains another
  // (§9.106: *"the more specific name is the one the caller bothered to say"*). Three characters
  // minimum, because a two-letter zone name appears in most Slovak addresses by accident.
  const haystack = fold(address)
  let named: T | null = null
  let namedLength = 0
  let contested = false
  if (haystack !== '') {
    for (const zone of candidates) {
      const needle = fold(zone.name)
      if (needle.length < 3 || !haystack.includes(needle)) continue
      if (needle.length > namedLength) {
        named = zone
        namedLength = needle.length
        contested = false
      } else if (needle.length === namedLength) {
        contested = true
      }
    }
  }
  if (named !== null && !contested) return named

  // Step 4 - the depot, then the smallest. Both deterministic; the second needs nothing but the zone
  // rows, so the answer never depends on whether the geocoder was up.
  let best: T | null = null
  let bestKey = Number.MAX_VALUE
  for (const zone of candidates) {
    const key =
      depot === null
        ? (zone.radius ?? Number.MAX_VALUE)
        : metresToCentre(zone, depot.lat, depot.lng)
    if (key < bestKey) {
      bestKey = key
      best = zone
    }
  }
  return best
}


/**
 * What a zone's geometry needs, whichever of the two zone types it comes from - §9.799.
 *
 * The manager's `DeliveryZone` declares `center` optional and the customer checkout's own declares it nullable, so
 * this asks for the shape alone, the way `ZoneTieBreakInput` does for the tie-break.
 */
export interface ZoneShape {
  id: string
  type: string
  center?: LatLng | null
  radius?: number | null
  polygon?: LatLng[] | null
}

/** One degree of latitude in metres - the server's `111_320d`, and the checkout's `METRES_PER_DEGREE`. */
const METRES_PER_DEGREE = 111_320

/**
 * The area a circle zone is actually served over - §9.799.
 *
 * `DeliveryZoneService.withinRadius` measures degrees as flat metres, deliberately (its javadoc: the zones were drawn
 * against that arithmetic), so the served area is a circle **in degrees**: a north-south ellipse about two thirds as
 * wide as a true circle at Slovakia's latitude. The customer's zone map draws this, so a click on it and the checkout's
 * `zoneFor` cannot disagree about whether a spot is inside.
 */
export function servedCircleOutline(center: LatLng, radiusMetres: number, points = 72): LatLng[] {
  const degrees = radiusMetres / METRES_PER_DEGREE
  return Array.from({ length: points }, (_, index) => {
    const angle = (2 * Math.PI * index) / points
    return { lat: center.lat + degrees * Math.sin(angle), lng: center.lng + degrees * Math.cos(angle) }
  })
}

/**
 * The outline a zone is drawn with on both maps - §9.800: a circle as the area the server serves
 * (`servedCircleOutline`), a polygon as its corners, and nothing for a zone with neither. One function, so the zone
 * editor and the customer's *Oblasti doručenia* cannot draw one zone two ways.
 */
export function servedOutline(zone: ZoneShape): LatLng[] {
  if (zone.type === 'circle') {
    if (zone.center === null || zone.center === undefined) return []
    if (zone.radius === null || zone.radius === undefined || zone.radius <= 0) return []
    return servedCircleOutline(zone.center, zone.radius)
  }
  if (zone.type === 'polygon') return zone.polygon ?? []
  return []
}

/**
 * Whether the area a zone serves holds the point - §9.800. A circle by flat degrees, as `DeliveryZoneService`
 * measures it and both maps draw it; a polygon by its corners.
 */
function servedShapeHolds(zone: ZoneShape, point: LatLng): boolean {
  if (zone.type === 'circle') {
    if (zone.center === null || zone.center === undefined || zone.radius === null || zone.radius === undefined) {
      return false
    }
    return Math.hypot(point.lat - zone.center.lat, point.lng - zone.center.lng) * METRES_PER_DEGREE <= zone.radius
  }
  if (zone.type === 'polygon') {
    const corners = zone.polygon ?? []
    return corners.length >= MINIMUM_POLYGON_POINTS && isPointInPolygon(point, corners)
  }
  return false
}

/** Roughly how much ground a zone serves, in square metres - only ever compared with another zone's. */
function servedArea(zone: ZoneShape): number {
  // A flat-degree circle is an ellipse: the radius north-south, the radius times cos(latitude) east-west.
  if (zone.type === 'circle') {
    return Math.PI * (zone.radius ?? 0) ** 2 * Math.cos(toRadians(zone.center?.lat ?? 0))
  }
  const corners = zone.polygon ?? []
  if (corners.length < MINIMUM_POLYGON_POINTS) return 0
  const lngScale = METRES_PER_DEGREE * Math.cos(toRadians(corners[0]?.lat ?? 0))
  let twice = 0
  for (let index = 0, previous = corners.length - 1; index < corners.length; previous = index++) {
    const here = corners[index]!
    const before = corners[previous]!
    twice += before.lng * lngScale * (here.lat * METRES_PER_DEGREE) - here.lng * lngScale * (before.lat * METRES_PER_DEGREE)
  }
  return Math.abs(twice) / 2
}

/**
 * Every zone whose served area holds the point, smallest first - §9.799, the manager's map.
 *
 * **The area as drawn**: since §9.800 the editor draws a circle as the area it serves, so a click picks what the manager
 * can see under the cursor. Disabled zones count - they are drawn, faintly, and are editable. Smallest first, so a zone
 * inside a bigger one is the first answer rather than unreachable under it.
 */
export function zonesAtPoint<T extends ZoneShape>(point: LatLng, zones: readonly T[]): T[] {
  return zones
    .filter((zone) => servedShapeHolds(zone, point))
    .map((zone, order) => ({ zone, area: servedArea(zone), order }))
    .sort((left, right) => left.area - right.area || left.order - right.order)
    .map((entry) => entry.zone)
}

/**
 * The zone a click on the manager's map selects - §9.799.
 *
 * The smallest zone under the point, or - when the zone already selected is one of them - the next one, wrapping, so a
 * second click on the same spot reaches every overlapping zone. `null` when no zone holds the point, which the page
 * reads as *change nothing*.
 */
export function zoneToSelectAt<T extends ZoneShape>(
  point: LatLng,
  zones: readonly T[],
  selectedId: string | null,
): T | null {
  const under = zonesAtPoint(point, zones)
  if (under.length === 0) return null
  const current = under.findIndex((zone) => zone.id === selectedId)
  return current === -1 ? (under[0] ?? null) : (under[(current + 1) % under.length] ?? null)
}

/** What `zoneServing` needs of a zone: its shape, the tie-break's name and circle, and whether it is switched on. */
export interface ServingZoneInput extends ZoneShape, ZoneTieBreakInput {
  enabled?: boolean | null
}

/**
 * The zone that serves a point - §9.801, owner O148: *"Make sure nová objednávka picks the zone the same way"*.
 *
 * The server's rule (`DeliveryZoneService`): the enabled zones whose served area holds the point - a circle by flat
 * degrees, a polygon by its corners - settled by `pickZone` with the address the order carries and the depot. The
 * checkout's `zoneFor` and the order builder both ask this, so a phone order and a web order to one spot get one zone.
 */
export function zoneServing<T extends ServingZoneInput>(
  point: LatLng,
  zones: readonly T[],
  options: { address: string | null; depot: LatLng | null },
): T | null {
  const containing = zones.filter((zone) => zone.enabled !== false && servedShapeHolds(zone, point))
  return pickZone(containing, options.address, options.depot)
}

/** One zone as a map draws it: its id, its colour and the outline of the area it serves. */
export interface ZoneOutline {
  id: string
  color: string
  outline: LatLng[]
}

/** The colour a zone is drawn in when its row carries none - the editor's first `ZONE_COLORS` blue. */
const DEFAULT_OUTLINE_COLOR = '#3b82f6'

/** `{ lat, lng }` with two finite numbers, or undefined. */
function payloadPoint(value: unknown): LatLng | undefined {
  const row = value as { lat?: unknown; lng?: unknown } | null | undefined
  if (typeof row?.lat !== 'number' || typeof row.lng !== 'number') return undefined
  if (!Number.isFinite(row.lat) || !Number.isFinite(row.lng)) return undefined
  return { lat: row.lat, lng: row.lng }
}

/**
 * The franchise's zones as the pin maps draw them - §9.802, owner O149.
 *
 * Read off `GET /delivery-zones` as it arrives: the **enabled** zones with a shape, each in its own colour (or the default
 * blue) and as the area it serves (`servedOutline`), so a pin inside one is in it by the server's rule. A switched-off
 * zone serves nothing and is not drawn; a zone with no shape has nothing to draw. Shared, so the checkout's map and
 * *Nová objednávka*'s cannot draw one zone two ways.
 */
export function zoneOutlinesOf(payload: unknown): ZoneOutline[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'string' || row.id === '' || row.enabled === false) return []
    if (row.type !== 'circle' && row.type !== 'polygon') return []
    const polygon = Array.isArray(row.polygon)
      ? row.polygon.flatMap((corner) => {
          const point = payloadPoint(corner)
          return point === undefined ? [] : [point]
        })
      : null
    const outline = servedOutline({
      id: row.id,
      type: row.type,
      center: payloadPoint(row.center) ?? null,
      radius: typeof row.radius === 'number' ? row.radius : null,
      polygon,
    })
    if (outline.length < MINIMUM_POLYGON_POINTS) return []
    return [{ id: row.id, color: typeof row.color === 'string' && row.color !== '' ? row.color : DEFAULT_OUTLINE_COLOR, outline }]
  })
}
